import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import { projectsAPI, tasksAPI } from '../services/api';
import TaskCard from '../components/Tasks/TaskCard';
import TaskForm from '../components/Tasks/TaskForm';
import TaskDetails from '../components/Tasks/TaskDetails';
import { toast } from 'react-toastify';

const ProjectDetails = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [tabValue, setTabValue] = useState('all');

  useEffect(() => {
    fetchProjectAndTasks();
  }, [id]);

  useEffect(() => {
    filterTasks();
  }, [tasks, tabValue]);

  const fetchProjectAndTasks = async () => {
    try {
      setLoading(true);
      const [projectRes, tasksRes] = await Promise.all([
        projectsAPI.getOne(id),
        tasksAPI.getProjectTasks(id),
      ]);
      setProject(projectRes.data.data);
      setTasks(tasksRes.data.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filterTasks = () => {
    let filtered = tasks;
    if (tabValue !== 'all') {
      filtered = tasks.filter((task) => task.status === tabValue);
    }
    setFilteredTasks(filtered);
  };

  const handleCreateTask = () => {
    setSelectedTask(null);
    setFormOpen(true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setFormOpen(true);
    setDetailsOpen(false);
  };

  const handleDeleteTask = (task) => {
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await tasksAPI.delete(taskToDelete._id);
      toast.success('Task deleted successfully');
      fetchProjectAndTasks();
      setDeleteDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete task');
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTaskId(task._id);
    setDetailsOpen(true);
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4">{project?.name}</Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              {project?.description}
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreateTask}>
            New Task
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label="All Tasks" value="all" />
          <Tab label="To Do" value="todo" />
          <Tab label="In Progress" value="in-progress" />
          <Tab label="Review" value="review" />
          <Tab label="Completed" value="completed" />
        </Tabs>

        {filteredTasks.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary">
              No tasks found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Create your first task to get started
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredTasks.map((task) => (
              <Grid item xs={12} sm={6} md={4} key={task._id}>
                <TaskCard
                  task={task}
                  onEdit={handleEditTask}
                  onDelete={handleDeleteTask}
                  onClick={handleTaskClick}
                />
              </Grid>
            ))}
          </Grid>
        )}

        <TaskForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          projectId={id}
          task={selectedTask}
          onSuccess={() => {
            fetchProjectAndTasks();
            toast.success(selectedTask ? 'Task updated' : 'Task created');
          }}
        />

        <TaskDetails
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          taskId={selectedTaskId}
          onEdit={handleEditTask}
        />

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Task</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{taskToDelete?.title}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default ProjectDetails;
